// A saved studio dashboard, read only.
//
// The counterpart of `CanvasGrid`, and the split is about where the DATA comes
// from rather than about whether there are handles: a canvas previews each tile
// separately because half of them are specs nobody has stored yet, while a
// saved dashboard is one `POST /mine/dashboards/{id}/query` for the whole
// board — one budget charge, one cache probe, one settle batch, exactly like an
// authored dashboard. Two data paths, one `ChartCard`.
import type {
  ChartSpec,
  DashboardLayout,
  DatasetSpec,
  FilterState,
  NativeFilterSpec,
} from "@bi/shared/types";

import type { ChartRenderState } from "../../lib/bi-query";
import { columnSpans } from "../../lib/dashboard-layout";
import { isChartTile, isMarkdownTile } from "../../lib/studio-model";
import { FilterBar } from "../filter-bar";
import { Markdown } from "../markdown";
import { StudioTile } from "./studio-tile";

export interface StudioDashboardViewProps {
  layout: DashboardLayout;
  controls: readonly NativeFilterSpec[];
  charts: Readonly<Record<string, ChartSpec>>;
  datasets: Readonly<Record<string, DatasetSpec>>;
  chartStates: Readonly<Record<string, ChartRenderState>>;
  /** chartId -> its mark set's chart id, as the query response ordered them
   *  (`annotationIdsByChart`). The marks are already in `chartStates` under
   *  that id — the backend fans them out with their tile — so this is a lookup,
   *  not a second fetch. */
  annotationIds?: Readonly<Record<string, string>>;
  filters: FilterState;
  onFiltersChange: (next: FilterState) => void;
  employeeEmail: string;
  emptyExplain?: (chart: ChartSpec) => string;
  onRetry: (chartId: string) => void;
}

export function StudioDashboardView({
  layout,
  controls,
  charts,
  datasets,
  chartStates,
  annotationIds = {},
  filters,
  onFiltersChange,
  employeeEmail,
  emptyExplain,
  onRetry,
}: StudioDashboardViewProps) {
  return (
    <>
      {controls.length > 0 ? (
        // The SAME filter bar an authored dashboard draws, over the same
        // `NativeFilterSpec` list — which is why a studio control gets URL
        // serialization, per-chart auto-scoping and the inactive-here note for
        // free rather than as three more things to build.
        <FilterBar
          filters={controls}
          chartStates={chartStates}
          charts={charts as Record<string, ChartSpec>}
          datasets={datasets as Record<string, DatasetSpec>}
          state={filters}
          onChange={onFiltersChange}
        />
      ) : null}
      {layout.rows.map((row, rowIndex) => {
        const spans = columnSpans(row.tiles);
        return (
          <div key={rowIndex} className="bi-card-grid">
            {row.tiles.map((tile, index) => {
              if (isMarkdownTile(tile)) {
                return (
                  <div
                    key={`${rowIndex}:${index}`}
                    className="bi-studio-text"
                    style={{ gridColumn: `span ${spans[index] ?? tile.width}` }}
                  >
                    <Markdown source={tile.markdown} />
                  </div>
                );
              }
              if (!isChartTile(tile)) return null;
              const chart = charts[tile.chart_uuid];
              if (!chart) return null;
              return (
                <StudioTile
                  key={`${rowIndex}:${index}`}
                  chart={chart}
                  state={chartStates[chart.uuid] ?? { status: "loading" }}
                  annotationState={
                    annotationIds[chart.uuid]
                      ? (chartStates[annotationIds[chart.uuid] as string] ?? { status: "loading" })
                      : null
                  }
                  columnSpan={spans[index] ?? tile.width}
                  width={tile.width}
                  dataset={datasets[chart.dataset_uuid]}
                  filters={filters}
                  employeeEmail={employeeEmail}
                  emptyExplanation={emptyExplain?.(chart)}
                  onRetry={() => onRetry(chart.uuid)}
                />
              );
            })}
          </div>
        );
      })}
    </>
  );
}
